// useBackground.js

import {ref, watch} from "vue";
import {useDisplayMode} from "@/composables/useDisplayMode";

const log = (msg) => console.log(`useBackground: ${msg}`); // eslint-disable-line
const displayMode = useDisplayMode(); // single value returned from composable
const callToActionStyle = ref({
  height: "0px",
  backgroundColor: "#000000"
});
let imageURL; // currently active background image URL
let imageLoaded = false; // true once imageURL has been successfully fetched
// eslint-disable-next-line no-unused-vars
let pendingImage = null; // held to prevent Safari GC during image load

// Register the displayMode watcher once at module level (not inside setBackgroundImage)
watch(displayMode, () => applyGradient(imageURL));

function setCallToActionStyle(newHeight, newBackgroundColor) {
  log(`CTA [height, backgroundColor] = [${newHeight}, ${newBackgroundColor}]`);
  callToActionStyle.value.height = newHeight;
  callToActionStyle.value.backgroundColor = newBackgroundColor;
  applyGradient(imageURL);
}

function callToActionBanner() {
  const backgroundColor = callToActionStyle.value.backgroundColor;
  const height = callToActionStyle.value.height;
  return `${backgroundColor} 0%, ${backgroundColor} ${height}`;
}

function gradient(imageUrl) {
  const style = getComputedStyle(document.body);
  const end = style.getPropertyValue("--background-color");
  let start;
  if (imageUrl) {
    log(`image URL = ${imageUrl}`);
    start = "rgba(0, 0, 0, 0)";
    imageUrl = `, url(${imageUrl})`;
  } else {
    start = style.getPropertyValue("--top-of-page");
    imageUrl = "";
  }
  const banner = callToActionBanner();
  const height = callToActionStyle.value.height;
  const grad = `${start} ${height}, ${end} 500px`;
  if (callToActionStyle.value.height === "0px") {
    log(`remove CTA banner from background`);
    return `linear-gradient(${grad})${imageUrl}`;
  }
  log(`add CTA banner to background`);
  return `linear-gradient(${banner}, ${grad})${imageUrl}`;
}

function applyFallback() {
  // Apply immediate fallback colour before image loads.
  // Dark mode: near-black. Light mode: white (the CSS default).
  const isDark = displayMode.value === "darkMode";
  document.body.style.backgroundColor = isDark ? "#202124" : "white";
  document.body.style.backgroundImage = gradient("");
}

function applyImageStyle(url) {
  const style = getComputedStyle(document.body);
  const end = style.getPropertyValue("--background-color");
  const height = callToActionStyle.value.height;
  document.body.style.backgroundColor = end;
  document.body.style.backgroundImage = gradient(url);
  document.body.style.backgroundRepeat = "no-repeat, repeat-x";
  document.body.style.backgroundPosition = `top, top ${height} left 0`;
  document.body.style.backgroundSize = `cover, contain`;
}

function gradientNoImage() {
  document.body.style.backgroundImage = gradient("");
}

// Load image then apply. Handles Safari's cached-image edge case by checking
// .complete synchronously after setting .src (Safari may skip the load event
// for disk-cached resources).
function loadAndApply(url) {
  const img = new Image();
  pendingImage = img; // module-level ref prevents Safari GC before onload fires
  let applied = false;
  function apply() {
    if (applied) return;
    applied = true;
    pendingImage = null;
    imageLoaded = true;
    log(`image loaded: ${url}`);
    applyImageStyle(url);
  }
  img.onload = apply;
  img.onerror = () => {
    pendingImage = null;
    console.warn(`Error loading background image: ${url}`);
    gradientNoImage();
  };
  img.src = url;
  // Safari sometimes skips onload for cached images — check synchronously
  if (img.complete) apply();
}

function applyGradient(url) {
  log(`applyGradient(${url})`);
  if (!url) {
    gradientNoImage();
    return;
  }
  // Once the image is confirmed loaded, re-apply styles directly without
  // creating a new Image object. This prevents concurrent in-flight Image
  // objects that Safari may GC before onload fires.
  if (imageLoaded) {
    applyImageStyle(url);
    return;
  }
  loadAndApply(url);
}

function setBackgroundImage(year) {
  log(`year = ${year}`);
  const newImageURL = `/${year}/Background.jpg`;
  // Apply fallback colour immediately so body is never white on dark mode
  applyFallback();
  if (newImageURL === imageURL) {
    // Same URL: re-apply in case displayMode changed or DOM was reset
    applyGradient(imageURL);
    return;
  }
  imageURL = newImageURL;
  imageLoaded = false; // reset: new URL must be fetched before imageLoaded is true
  log(`imageUrl = ${imageURL}`);
  applyGradient(imageURL);
}

// Defensive safeguard: call after mount to guarantee the page is not stuck in
// a broken state. Exits immediately if the background is already correct.
// Safe to call multiple times (idempotent).
function ensureBackground() {
  if (!imageURL) return;
  const bg = document.body.style.backgroundImage;
  const isApplied = bg && bg.includes(imageURL);
  if (isApplied) return;
  log(`ensureBackground: background not applied — recovering`);
  applyFallback();
  if (imageLoaded) {
    applyImageStyle(imageURL);
    return;
  }
  // Load appears stuck: clear any partial state and retry
  pendingImage = null;
  loadAndApply(imageURL);
}

export {setCallToActionStyle, setBackgroundImage, ensureBackground};
